import { ProcessedOrderbookEntry } from './types';

import { aggregateOrdersByTickSize } from '../lib/aggregateOrders';
import { calculateOrderbookPercentages } from '../lib/calculatePercentages';

import { OrderbookStoreState } from './orderbook.store';

/**
 * Selectors for deriving processed orderbook data from store
 * These selectors can be used with useOrderbookStore for optimal performance
 */

// Display depth based on view mode
// Note: This is for UI display only, NOT for data fetching
// WebSocket depth is exchange-specific and may change with tick size selection
// When showing both asks and bids, limit each side to avoid crowding
// When showing single side, allow more depth for better visibility
const DISPLAY_DEPTH_BOTH_SIDES = 30; // Per side when viewMode='all'
const DISPLAY_DEPTH_SINGLE_SIDE = 50; // When viewMode='ask' or 'bid'

/**
 * Pad orderbook array to fixed depth with placeholder rows
 *
 * Why padding?
 * - Maintains consistent visual height even with sparse data
 * - Prevents layout shift when levels are added/removed
 * - Enables smooth CSS transitions for row changes
 * - Placeholder rows have zero amount and are visually dimmed
 *
 * CRITICAL VIRTUALIZATION FIX:
 * - Virtualization uses absolute positioning (not flexbox)
 * - For asks: prepend placeholders so real data appears at bottom of scroll
 * - For bids: append placeholders so real data appears at top of scroll
 *
 * @param entries - Actual orderbook entries
 * @param side - 'ask' or 'bid' (determines placeholder position)
 * @param targetDepth - Desired array length (from user settings)
 * @returns Padded array with placeholders in correct position
 */
function padToFixedDepth(
  entries: ProcessedOrderbookEntry[],
  side: 'ask' | 'bid',
  targetDepth: number,
): ProcessedOrderbookEntry[] {
  if (entries.length >= targetDepth) {
    // Already at or above target, trim to exact depth
    return entries.slice(0, targetDepth);
  }

  // Create placeholder rows for missing depth
  const placeholders: ProcessedOrderbookEntry[] = [];
  const placeholdersNeeded = targetDepth - entries.length;

  for (let i = 0; i < placeholdersNeeded; i++) {
    placeholders.push({
      price: `placeholder-${side}-${i}`, // Unique key for React (include side to avoid collisions)
      amount: '0',
      total: '0',
      currentAmount: 0,
      percentage: 0,
      isPlaceholder: true, // Flag for visual styling
    });
  }

  // CRITICAL: Position placeholders based on side
  // Asks: prepend placeholders (real data scrolls to bottom, near spread)
  // Bids: append placeholders (real data stays at top, near spread)
  if (side === 'ask') {
    return [...placeholders, ...entries];
  } else {
    return [...entries, ...placeholders];
  }
}

/**
 * Select processed asks for a pair with current settings applied
 *
 * Processing pipeline:
 * 1. Aggregate ALL data by tick size (groups price levels from all 5000 levels, keeps asks ascending)
 * 2. Calculate percentages and cumulative values (keeps incoming order)
 * 3. Slice to display depth based on viewMode (30 for 'all', 50 for single side)
 * 4. Prepend placeholders for consistent layout
 *
 * Returns ascending order: [0.3440, 0.3441, ..., 0.3450]
 * UI layer reverses this for display with auto-scroll to show lowest ask at bottom
 */
export const selectProcessedAsks = (
  state: OrderbookStoreState,
  pairId: number,
): ProcessedOrderbookEntry[] => {
  const rawData = state.orderbooks[pairId];
  const settings = state.settings[pairId];

  // Determine display depth based on view mode
  const viewMode = settings?.viewMode ?? 'all';
  const displayDepth =
    viewMode === 'all'
      ? DISPLAY_DEPTH_BOTH_SIDES
      : viewMode === 'ask'
        ? DISPLAY_DEPTH_SINGLE_SIDE
        : 0; // Don't show asks when viewMode='bid'

  // Return empty array when no data or asks not visible in current view mode
  if (!rawData?.asks || rawData.asks.length === 0 || !settings || displayDepth === 0) {
    return padToFixedDepth([], 'ask', displayDepth);
  }

  // Step 1: Aggregate ALL data by tick size (returns descending: highest first)
  const aggregated = aggregateOrdersByTickSize(rawData.asks, settings.tickSize, 'ask');

  // Step 2: Calculate percentages and cumulative values on ALL aggregated data
  const processed = calculateOrderbookPercentages(aggregated, 'ask');

  // Step 3: Slice to display depth (keep order from percentage calculation)
  // Don't reverse here - percentage calculation already sorted correctly!
  // Reversal happens in UI layer for display only
  const sliced = processed.slice(0, displayDepth);

  // Step 5: Pad to display depth for consistent layout
  // For asks: prepend placeholders so real data appears at bottom (near spread)
  const result = padToFixedDepth(sliced, 'ask', displayDepth);

  return result;
};

/**
 * Select processed bids for a pair with current settings applied
 *
 * Processing pipeline:
 * 1. Aggregate ALL data by tick size (groups price levels from all 5000 levels)
 * 2. Calculate percentages and cumulative values on ALL aggregated data
 * 3. Slice to display depth based on viewMode (30 for 'all', 50 for single side)
 * 4. Pad to display depth for consistent layout
 * 5. Keep descending order (highest bid first)
 *
 * Bids are displayed top-down (highest bid at top, near spread)
 */
export const selectProcessedBids = (
  state: OrderbookStoreState,
  pairId: number,
): ProcessedOrderbookEntry[] => {
  const rawData = state.orderbooks[pairId];
  const settings = state.settings[pairId];

  // Determine display depth based on view mode
  const viewMode = settings?.viewMode ?? 'all';
  const displayDepth =
    viewMode === 'all'
      ? DISPLAY_DEPTH_BOTH_SIDES
      : viewMode === 'bid'
        ? DISPLAY_DEPTH_SINGLE_SIDE
        : 0; // Don't show bids when viewMode='ask'

  // Return empty array when no data or bids not visible in current view mode
  if (!rawData?.bids || rawData.bids.length === 0 || !settings || displayDepth === 0) {
    return padToFixedDepth([], 'bid', displayDepth);
  }

  // Step 1: Aggregate ALL data by tick size
  const aggregated = aggregateOrdersByTickSize(rawData.bids, settings.tickSize, 'bid');

  // Step 2: Calculate percentages and cumulative values on ALL aggregated data
  const processed = calculateOrderbookPercentages(aggregated, 'bid');

  // Step 3: Slice to display depth (only now we limit the data for UI)
  const sliced = processed.slice(0, displayDepth);

  // Step 4: Pad to display depth for consistent layout
  // For bids: append placeholders so real data stays at top (near spread)
  const result = padToFixedDepth(sliced, 'bid', displayDepth);

  return result;
};

/**
 * Select last price for a pair
 */
export const selectLastPrice = (state: OrderbookStoreState, pairId: number): string => {
  return state.orderbooks[pairId]?.lastPrice || '0';
};

/**
 * Select loading state for a pair
 */
export const selectIsLoading = (state: OrderbookStoreState, pairId: number): boolean => {
  return state.loading[pairId] || false;
};

/**
 * Select error state for a pair
 */
export const selectError = (state: OrderbookStoreState, pairId: number): Error | null => {
  return state.errors[pairId] || null;
};

/**
 * Select current settings for a pair
 */
export const selectSettings = (state: OrderbookStoreState, pairId: number) => {
  return state.settings[pairId];
};

/**
 * Select tick size for a pair
 */
export const selectTickSize = (state: OrderbookStoreState, pairId: number): number | null => {
  return state.settings[pairId]?.tickSize ?? null;
};

/**
 * Select base tick size (initial minimum from exchange) for a pair
 * This value is set once during initialization and never changes
 */
export const selectBaseTickSize = (state: OrderbookStoreState, pairId: number): number | null => {
  return state.settings[pairId]?.baseTickSize ?? null;
};

/**
 * Select view mode for a pair
 */
export const selectViewMode = (state: OrderbookStoreState, pairId: number) => {
  return state.settings[pairId]?.viewMode ?? 'all';
};
